import React, { useState, useCallback } from "react";
import { useChartTextColor } from "../hooks/useChartTextColor";
import CardLoading from "./CardLoading";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { ScoreSummaryItem } from "../types";

interface ScoreDistributionChartProps {
  data: ScoreSummaryItem[] | undefined;
  isLoading: boolean;
  title: string;
  onExpand?: () => void;
}

// Fallback colors when backend doesn't provide color
const SCORE_PIE_COLORS: Record<string, string> = {
  Clean: "#70ad47",
  High: "#ff0000",
  Medium: "#ffc000",
  None: "#70ad47",
  Low: "#92d050",
  Flagged: "#ff0000",
  Processing: "#5b9bd5",
};

const HEADER_STYLE = { backgroundColor: "var(--navbar-and-treeview-bg)", color: "white" };

// Pie slice label matching MVC: "Category:\nXX%" (no decimals, on-slice style)
function createRenderLabel(labelColor: string) {
  return ({ cx, cy, midAngle, outerRadius, payload }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 30;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={labelColor}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        style={{ fontSize: 12 }}
      >
        {`${payload.category}:`}
        <tspan x={x} dy={16} style={{ fontWeight: "bold" }}>
          {`${payload.percentage}%`}
        </tspan>
      </text>
    );
  };
}

const ScoreDistributionChart: React.FC<ScoreDistributionChartProps> = ({
  data,
  isLoading,
  title,
  onExpand,
}) => {
  const textColor = useChartTextColor();
  const renderLabel = createRenderLabel(textColor);
  // Track hidden legend items — clicking toggles slice visibility (matches Kendo legend behavior)
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());

  const handleLegendClick = useCallback((entry: { value?: string }) => {
    const key = entry.value;
    if (!key) return;
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-header" style={HEADER_STYLE}>
          <span className="panel-title">{title}</span>
        </div>
        <div className="card-body">
          <CardLoading />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="card">
        <div className="card-header" style={HEADER_STYLE}>
          <span className="panel-title">{title}</span>
        </div>
        <div className="card-body">
          <p className="text-muted">No data available</p>
        </div>
      </div>
    );
  }

  // Use backend color when available, fall back to local map
  const coloredData = data.map((item) => ({
    ...item,
    color: item.color || SCORE_PIE_COLORS[item.category] || "#8884d8",
  }));

  // Filter out hidden categories for pie rendering (Kendo hides the slice entirely)
  const visibleData = coloredData.filter((item) => !hiddenCategories.has(item.category));

  // Custom legend with clickable items matching Kendo toggle behavior
  const renderLegend = (props: { payload?: Array<{ value: string; color: string }> }) => {
    const { payload } = props;
    if (!payload) return null;
    return (
      <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
        {payload.map((entry) => {
          const isHidden = hiddenCategories.has(entry.value);
          return (
            <span
              key={entry.value}
              onClick={() => handleLegendClick(entry)}
              style={{
                cursor: "pointer",
                color: isHidden ? "#999" : textColor,
                textDecoration: isHidden ? "line-through" : "none",
                fontSize: 12,
                userSelect: "none",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  backgroundColor: isHidden ? "#ccc" : entry.color,
                  marginRight: 4,
                  verticalAlign: "middle",
                }}
              />
              {entry.value}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header" style={HEADER_STYLE}>
        <span className="panel-title">{title}</span>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            {/* Pie sized by percentage matching MVC field: "percentage" */}
            <Pie
              data={visibleData}
              dataKey="percentage"
              nameKey="category"
              cx="50%"
              cy="45%"
              outerRadius={110}
              label={renderLabel}
              labelLine={true}
            >
              {visibleData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            {/* Tooltip matching MVC: "Category - XX% (YYY records)" */}
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const item = payload[0].payload;
                return (
                  <div style={{ background: "#fff", color: "#333", border: "1px solid #ccc", padding: "6px 10px", fontSize: 12 }}>
                    {item.category} - {item.percentage}% ({item.value} records)
                  </div>
                );
              }}
            />
            {/* Legend at bottom with clickable toggle — uses all data (not filtered) so hidden items still show */}
            <Legend
              verticalAlign="bottom"
              align="center"
              payload={coloredData.map((item) => ({
                value: item.category,
                type: "rect" as const,
                color: hiddenCategories.has(item.category) ? "#ccc" : item.color,
              }))}
              content={renderLegend}
            />
          </PieChart>
        </ResponsiveContainer>
        {onExpand && (
          <a href="#" onClick={(e) => { e.preventDefault(); onExpand(); }}>
            <img src="/images/dashboard/expand-icon.svg" className="expand" alt="expand" />
          </a>
        )}
      </div>
    </div>
  );
};

export default ScoreDistributionChart;
